import {
  getLocalStorage,
  setLocalStorage,
} from "@/utils/localstorage";

import {
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

interface CartState {
  cart_products: any[];
  orderQuantity: number;
}

const initialState: CartState = {
  cart_products: [],
  orderQuantity: 1,
};

export const cartSlice = createSlice({
  name: "cart",

  initialState,

  reducers: {
    /* =====================================================
       ADD TO CART
    ===================================================== */

    add_cart_product: (
      state,
      action: PayloadAction<any>
    ) => {
      const productId =
        action.payload?.id;

      const isExist =
        state.cart_products.some(
          (item) =>
            String(item.id) ===
            String(productId)
        );

      if (
        Number(action.payload?.quantity) ===
        0
      ) {
        return;
      }

      /* =================================================
         NEW PRODUCT
      ================================================= */

      if (!isExist) {
        const newItem = {
          ...action.payload,

          orderQuantity:
            Number(
              action.payload?.orderQuantity
            ) || 1,
        };

        state.cart_products.push(
          newItem
        );
      }

      /* =================================================
         EXISTING PRODUCT
      ================================================= */

      else {
        state.cart_products.forEach(
          (item) => {
            if (
              String(item.id) ===
              String(productId)
            ) {
              const currentQuantity =
                Number(
                  item.orderQuantity
                ) || 0;

              const addQuantity =
                state.orderQuantity !== 1
                  ? state.orderQuantity
                  : 1;

              item.orderQuantity =
                currentQuantity +
                addQuantity;
            }
          }
        );
      }

      setLocalStorage(
        "cart_products",
        state.cart_products
      );
    },

    /* =====================================================
       GLOBAL QUANTITY +
    ===================================================== */

    increment: (state) => {
      state.orderQuantity =
        Number(
          state.orderQuantity
        ) + 1;
    },

    /* =====================================================
       GLOBAL QUANTITY -
    ===================================================== */

    decrement: (state) => {
      state.orderQuantity =
        state.orderQuantity > 1
          ? state.orderQuantity - 1
          : 1;
    },

    /* =====================================================
       CART PRODUCT DECREMENT

       3 -> 2
       2 -> 1

       Quantity 1 থেকে remove করার কাজ
       GadgetProductCard থেকে remove_product
       দিয়ে করা হবে।
    ===================================================== */

    quantityDecrement: (
      state,
      action: PayloadAction<{
        id: string | number;
      }>
    ) => {
      state.cart_products.forEach(
        (item) => {
          if (
            String(item.id) ===
            String(action.payload.id)
          ) {
            const currentQuantity =
              Number(
                item.orderQuantity
              ) || 0;

            if (
              currentQuantity > 1
            ) {
              item.orderQuantity =
                currentQuantity - 1;
            }
          }
        }
      );

      setLocalStorage(
        "cart_products",
        state.cart_products
      );
    },

    /* =====================================================
       DIRECT SET QUANTITY

       User সরাসরি quantity input করতে পারবে।

       Example:
       1 -> 5
       5 -> 10
       10 -> 2
    ===================================================== */

    setCartQuantity: (
      state,
      action: PayloadAction<{
        id: string | number;
        quantity: number;
      }>
    ) => {
      const newQuantity = Math.max(
        0,
        Math.floor(
          Number(
            action.payload.quantity
          ) || 0
        )
      );

      state.cart_products.forEach(
        (item) => {
          if (
            String(item.id) ===
            String(action.payload.id)
          ) {
            item.orderQuantity =
              newQuantity;
          }
        }
      );

      /*
       * Quantity 0 হলে
       * পুরো product remove
       */

      state.cart_products =
        state.cart_products.filter(
          (item) =>
            Number(
              item.orderQuantity
            ) > 0
        );

      setLocalStorage(
        "cart_products",
        state.cart_products
      );
    },

    /* =====================================================
       REMOVE PRODUCT COMPLETELY
    ===================================================== */

    remove_product: (
      state,
      action: PayloadAction<{
        id: string | number;
        title?: string;
      }>
    ) => {
      state.cart_products =
        state.cart_products.filter(
          (item) =>
            String(item.id) !==
            String(action.payload.id)
        );

      setLocalStorage(
        "cart_products",
        state.cart_products
      );
    },

    /* =====================================================
       RESET GLOBAL QUANTITY
    ===================================================== */

    initialOrderQuantity: (
      state
    ) => {
      state.orderQuantity = 1;
    },

    /* =====================================================
       CLEAR CART
    ===================================================== */

    clearCart: (state) => {
      state.cart_products = [];

      state.orderQuantity = 1;

      setLocalStorage(
        "cart_products",
        state.cart_products
      );
    },

    /* =====================================================
       GET CART PRODUCTS
    ===================================================== */

    getCartProducts: (state) => {
      const products =
        getLocalStorage(
          "cart_products"
        );

      state.cart_products =
        Array.isArray(products)
          ? products
          : [];
    },
  },
});

/* =========================================================
   EXPORT ACTIONS
========================================================= */

export const {
  add_cart_product,
  increment,
  decrement,
  remove_product,
  quantityDecrement,
  setCartQuantity,
  initialOrderQuantity,
  clearCart,
  getCartProducts,
} = cartSlice.actions;

/* =========================================================
   REDUCER
========================================================= */

export default cartSlice.reducer;